import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { routerNavigatedAction } from '@ngrx/router-store';
import { combineLatest, from, merge, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, first, map, startWith, switchMap, tap } from 'rxjs/operators';

import { HttpInterventionGateway } from '../../../interventions/services/intervention-gateway.service';
import { InterventionActions } from '../../../interventions/store/actions/interventions';
import { PAGE_TYPES } from '../../../shared/pageType';
import { StoreService } from '../../../shared/services/store.service';
import { AppState } from '../../../shared/store/app-state';
import { GatewayId } from '../../../site-gateway/models/gateway';
import { SiteGatewayDetailPageActions } from '../../../site-gateway/store/actions/site-gateway-detail-page.actions';
import { getSiteByGatewayId } from '../../../site-list/store/selectors/sites';
import { SiteOwnerDetailPageActions } from '../../../site-owner/store/actions/site-owner-detail-page.actions';
import { DeviceMS } from '../../models/Device';
import { SetupId, Site } from '../../models/Site';
import { DEVICE_GATEWAY } from '../../services/DeviceGateway';
import { HttpSiteGateway } from '../../services/HttpSiteGateway';
import { SETUP_GATEWAY } from '../../services/SetupGateway';
import { buildDeviceFromRawDevice } from '../../utils/devices';
import { DeviceActions } from '../actions/device.actions';
import { GatewaysActions } from '../actions/gateways.actions';
import { SiteDetailPageActions } from '../actions/site-detail-page.actions';
import { SiteDevicesActions } from '../actions/site-devices.actions';
import { SiteHistoryPageActions } from '../actions/site-history-page.actions';
import { loadSiteInfo } from '../actions/site.actions';
import { setSelectedSite } from '../actions/ui.actions';
import { getSelectedDeviceId } from '../selectors/device-detail';
import { getDeviceItemList } from '../selectors/device-list';
import { getDefaultSiteGatewayId } from '../selectors/gateways';
import { getSiteDetailRouteContext } from '../selectors/router';
import { getSiteById } from '../selectors/site';

@Injectable()
export class SiteDetailEffects {
    private readonly httpInterventionService = inject(HttpInterventionGateway);
    private readonly actions$ = inject(Actions);
    private readonly store = inject(StoreService<AppState>);
    private readonly deviceGateway = inject(DEVICE_GATEWAY);
    private readonly siteGateway = inject(HttpSiteGateway);
    private readonly setupGateway = inject(SETUP_GATEWAY);
    private readonly router = inject(Router);

    private onSelectedSiteChanged = this.actions$.pipe(
        ofType(setSelectedSite),
        map(({ siteID }) => siteID),
        distinctUntilChanged(),
    );

    loadSiteOnPageOpened$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                SiteDetailPageActions.opened,
                SiteOwnerDetailPageActions.opened,
                SiteGatewayDetailPageActions.opened,
                SiteHistoryPageActions.opened,
            ),
            switchMap(({ gatewayId }) =>
                this.store
                    .select(getSiteByGatewayId(gatewayId))
                    .pipe(map((gatewaySite) => ({ gatewaySite, gatewayId }))),
            ),
            switchMap(({ gatewaySite, gatewayId }) => {
                if (gatewaySite) {
                    return of(setSelectedSite({ siteID: gatewaySite.id }));
                }
                return this.siteGateway
                    .getSiteByGatewayId(gatewayId)
                    .pipe(switchMap(([site]) => of(loadSiteInfo({ site }), setSelectedSite({ siteID: site.id }))));
            }),
        ),
    );

    loadSiteGatewaysOnPageOpened$ = createEffect(() =>
        this.onSelectedSiteChanged.pipe(
            switchMap((siteId) => this.store.select(getSiteById(siteId))),
            filter((site): site is Site & { setupId: SetupId } => site?.setupId != null),
            switchMap(({ id, setupId }) => combineLatest([of(id), this.setupGateway.fetchGateways(setupId)])),
            map(([siteId, gateways]) => GatewaysActions.fetchGatewaysListItemsSucceeded({ siteId, gateways })),
        ),
    );

    loadSiteDevicesOnPageOpened$ = createEffect(() =>
        this.onSelectedSiteChanged.pipe(
            switchMap((siteId) =>
                this.deviceGateway.getSiteDevices(siteId, true).pipe(
                    catchError(() => of<DeviceMS[]>([])),
                    map((devices) => SiteDevicesActions.devicesLoaded({ siteId, devices })),
                ),
            ),
        ),
    );

    loadSiteInterventionsOnPageOpened$ = createEffect(() =>
        this.onSelectedSiteChanged.pipe(
            switchMap((siteId) => this.httpInterventionService.getInterventions(siteId)),
            map((interventions) => InterventionActions.siteInterventionsLoaded({ interventions })),
        ),
    );

    loadGatewaysDevicesOnPageOpened$ = createEffect(() =>
        combineLatest([
            this.actions$.pipe(ofType(SiteDevicesActions.devicesLoaded)),
            this.actions$.pipe(ofType(GatewaysActions.fetchGatewaysListItemsSucceeded)),
        ]).pipe(
            switchMap(([{ devices: apiDevices }, { gateways }]) => {
                const gatewayIds = gateways.map((gateway) => gateway.gatewayId);

                const actions = apiDevices
                    .filter((apiDevices) => gatewayIds.includes(apiDevices.deviceurl))
                    .map((apiDevices) =>
                        DeviceActions.deviceLoaded({ device: buildDeviceFromRawDevice(apiDevices, apiDevices.id) }),
                    );

                return from(actions);
            }),
        ),
    );

    loadSelectedDevice$ = createEffect(() =>
        this.actions$.pipe(
            ofType(routerNavigatedAction),
            switchMap(() => this.store.select(getSelectedDeviceId)),
            distinctUntilChanged(),
            filter((deviceId): deviceId is string => deviceId != null),
            map((deviceId) => DeviceActions.deviceSelected({ deviceId })),
        ),
    );

    autoSelectFirstDevice$ = createEffect(
        () =>
            merge(
                this.actions$.pipe(ofType(routerNavigatedAction)),
                this.actions$.pipe(ofType(SiteDevicesActions.devicesLoaded)),
            ).pipe(
                switchMap(() =>
                    combineLatest([this.store.select(getSiteDetailRouteContext), this.store.select(getDeviceItemList)]),
                ),
                filter(
                    ([context, devices]) =>
                        context?.pageType === PAGE_TYPES.SITE_DETAIL && !context?.deviceId && devices.length > 0,
                ),
                tap(([context, devices]) => {
                    void this.router.navigate(['/gateway', context.gatewayId], {
                        queryParams: { deviceId: devices[0].deviceId },
                        replaceUrl: true,
                    });
                }),
            ),
        { dispatch: false },
    );

    selectDefaultGatewayWhenSiteEntered$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SiteDetailPageActions.entered),
                filter(({ deviceId }) => deviceId == null),
                switchMap(({ gatewayId }) =>
                    this.actions$.pipe(
                        ofType(SiteDevicesActions.devicesLoaded, GatewaysActions.fetchGatewaysListItemsSucceeded),
                        startWith(null),
                        switchMap(() => this.store.select(getDefaultSiteGatewayId)),
                        filter((defaultGatewayId): defaultGatewayId is GatewayId => defaultGatewayId != null),
                        first(),
                        filter((defaultGatewayId) => defaultGatewayId !== gatewayId),
                    ),
                ),
                tap((gatewayId) => {
                    void this.router.navigate(['/gateway', gatewayId], { replaceUrl: true });
                }),
            ),
        { dispatch: false },
    );
}
